import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import {
  fetchContexts,
  createContext,
  updateContext,
  enableContext,
  disableContext,
  deleteContext,
  recordContextFile,
  removeContextFile,
  editContextFile,
} from '@/api/contextsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import {
  extractContextUploadBundle,
  normalizeContextsList,
} from '@/utils/contexts'
import { uploadContextS3Object } from '@/utils/contextS3'

const SEARCH_FIELDS = [
  'context_name',
  'action_name',
  'status',
  'statusLabel',
  'createdOnLabel',
  'filesLabel',
]

function asFileList(files) {
  if (!files) return []
  if (Array.isArray(files)) return files.filter(Boolean)
  if (typeof FileList !== 'undefined' && files instanceof FileList) {
    return Array.from(files)
  }
  return [files].filter(Boolean)
}

function matchFileForKbEntry(fileList, kbFile) {
  const wanted = String(kbFile?.filename ?? '')
    .trim()
    .toLowerCase()
  if (!wanted) return null
  return (
    fileList.find(
      (file) => String(file?.name ?? '').trim().toLowerCase() === wanted,
    ) || null
  )
}

export function getContextsErrorMessage(payload) {
  return getApiMessage(payload, 'Failed to load products')
}

export function useContextsData() {
  const store = useStore()
  const route = useRoute()

  const contexts = ref([])
  const loading = ref(false)
  const page = ref(1)
  const itemsPerPage = ref(25)

  const isSessionReady = computed(() => store.getters.isSessionReady)
  const sessionContext = computed(() => store.getters.sessionContext)
  const tableSearch = computed(() => store.state.tableSearch)

  const filteredContexts = computed(() => {
    const q = String(tableSearch.value || '').trim().toLowerCase()
    if (!q) return contexts.value
    return contexts.value.filter((item) => {
      if (
        SEARCH_FIELDS.some((field) =>
          String(item[field] ?? '')
            .toLowerCase()
            .includes(q),
        )
      ) {
        return true
      }
      return (item.kb_files || []).some((file) =>
        String(file.filename ?? '')
          .toLowerCase()
          .includes(q),
      )
    })
  })

  const totalContexts = computed(() => filteredContexts.value.length)
  const activeCount = computed(
    () => filteredContexts.value.filter((item) => !item.disabled).length,
  )

  const pageCount = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return 1
    return Math.max(1, Math.ceil(filteredContexts.value.length / perPage))
  })

  const paginatedContexts = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return filteredContexts.value
    const start = (page.value - 1) * perPage
    return filteredContexts.value.slice(start, start + perPage)
  })

  const rangeLabel = computed(() => {
    const total = filteredContexts.value.length
    if (!total) return '0 of 0'
    const perPage = itemsPerPage.value
    if (perPage <= 0) return `1-${total} of ${total}`
    const start = (page.value - 1) * perPage + 1
    const end = Math.min(page.value * perPage, total)
    return `${start}-${end} of ${total}`
  })

  const isSearchActive = computed(() =>
    Boolean(String(tableSearch.value || '').trim()),
  )

  function buildListInput() {
    const { org_id } = sessionContext.value ?? {}
    return org_id ? { org_id } : null
  }

  async function loadContexts() {
    if (!isSessionReady.value) return { ok: false }

    const input = buildListInput()
    if (!input?.org_id) {
      contexts.value = []
      return {
        status: 'ERROR',
        message: 'Organisation is not ready.',
      }
    }

    loading.value = true
    try {
      const response = await fetchContexts(input)
      if (!isApiSuccess(response)) {
        contexts.value = []
        return response
      }

      contexts.value = normalizeContextsList(unwrapApiList(response))
      page.value = 1
      return { ok: true }
    } catch (error) {
      contexts.value = []
      return {
        status: 'ERROR',
        message: getContextsErrorMessage(error),
      }
    } finally {
      loading.value = false
    }
  }

  async function uploadAndRecordContextFiles({
    contextId,
    kbFiles,
    kbUploadMetadata,
    files,
  }) {
    const fileList = asFileList(files)
    if (!kbFiles.length) {
      return {
        status: 'ERROR',
        message: 'Product API did not return kb_files for upload.',
      }
    }

    for (const kbFile of kbFiles) {
      const s3Key = String(kbFile.s3_key ?? '').trim()
      const filename = String(kbFile.filename ?? '').trim()
      if (!s3Key || !filename) {
        return {
          status: 'ERROR',
          message: 'Product API returned an incomplete kb_files entry.',
        }
      }

      const file = matchFileForKbEntry(fileList, kbFile)
      if (!file) {
        return {
          status: 'ERROR',
          message: `Local file missing for "${filename}".`,
        }
      }

      try {
        await uploadContextS3Object({
          s3Key,
          data: file,
          contentType: file.type || undefined,
          metadata: kbUploadMetadata,
        })
      } catch (error) {
        return {
          status: 'ERROR',
          message: getApiMessage(
            error,
            `Failed to upload "${filename}" to storage.`,
          ),
        }
      }

      const recordResponse = await recordContextFile({
        context_id: contextId,
        s3_key: s3Key,
        filename,
      })

      if (!isApiSuccess(recordResponse)) {
        return {
          status: 'ERROR',
          message: getApiMessage(
            recordResponse,
            `Uploaded "${filename}" but failed to record it.`,
          ),
        }
      }
    }

    return { status: 'SUCCESS', message: 'Product files uploaded.' }
  }

  async function saveContextWithFiles({
    apiCall,
    contextIdHint = '',
    files,
    successMessage,
  }) {
    const fileList = asFileList(files)
    if (!fileList.length) {
      return {
        status: 'ERROR',
        message: 'At least one file is required.',
      }
    }

    const response = await apiCall(
      fileList.map((file) => ({ filename: file.name })),
    )

    if (!isApiSuccess(response)) {
      return response
    }

    const bundle = extractContextUploadBundle(response)
    const contextId = String(bundle.context_id || contextIdHint || '').trim()
    if (!contextId) {
      return {
        status: 'ERROR',
        message: 'Product API did not return context_id.',
      }
    }

    if (!bundle.kb_files.length) {
      return {
        status: 'ERROR',
        message: 'Product API did not return kb_files for upload.',
      }
    }

    const uploadResult = await uploadAndRecordContextFiles({
      contextId,
      kbFiles: bundle.kb_files,
      kbUploadMetadata: bundle.kb_upload_metadata,
      files: fileList,
    })

    if (!isApiSuccess(uploadResult)) {
      return uploadResult
    }

    return {
      status: 'SUCCESS',
      message: successMessage,
      data: {
        context_id: contextId,
        action_id: bundle.action_id || null,
        kb_s3_prefix: bundle.kb_s3_prefix || null,
        kb_files: bundle.kb_files,
      },
    }
  }

  async function saveContext({
    mode,
    context_id,
    context_name,
    files,
    replaceFile = null,
    greeting,
    mission_prompt,
    allow_manual_leads,
  }) {
    const name = String(context_name ?? '').trim()
    if (!name) {
      return { status: 'ERROR', message: 'Product name is required.' }
    }

    if (mode === 'EDIT') {
      const id = String(context_id ?? '').trim()
      if (!id) {
        return { status: 'ERROR', message: 'Product id is missing.' }
      }

      const fileList = asFileList(files)
      const replaceKey = String(replaceFile?.s3_key ?? '').trim()
      const actionFields = {
        greeting,
        mission_prompt,
        allow_manual_leads,
      }

      // Name-only (or action-field) update: no files → no upload envelope in response.
      if (!fileList.length) {
        return updateContext({
          context_id: id,
          context_name: name,
          ...actionFields,
        })
      }

      const uploadResult = await saveContextWithFiles({
        contextIdHint: id,
        files: fileList,
        successMessage: replaceKey
          ? 'Product file content updated.'
          : 'Product updated and files uploaded.',
        apiCall: (fileInputs) =>
          updateContext({
            context_id: id,
            context_name: name,
            files: fileInputs,
            ...actionFields,
          }),
      })

      if (!isApiSuccess(uploadResult)) {
        return uploadResult
      }

      // Replace flow: add new file first, then remove the old one by s3_key.
      if (replaceKey) {
        const removeResponse = await removeContextFile({
          context_id: id,
          s3_key: replaceKey,
        })
        if (!isApiSuccess(removeResponse)) {
          return {
            status: 'ERROR',
            message: getApiMessage(
              removeResponse,
              'New content uploaded, but failed to remove the previous file.',
            ),
          }
        }
      }

      return uploadResult
    }

    const { org_id } = sessionContext.value ?? {}
    if (!org_id) {
      return { status: 'ERROR', message: 'Organisation is not ready.' }
    }

    // createContext never accepts action fields — action is auto-created.
    return saveContextWithFiles({
      files,
      successMessage: 'Product created and files uploaded.',
      apiCall: (fileInputs) =>
        createContext({
          org_id,
          context_name: name,
          files: fileInputs,
        }),
    })
  }

  async function removeContextFileRecord({ context_id, s3_key, fileCount }) {
    const id = String(context_id ?? '').trim()
    const s3Key = String(s3_key ?? '').trim()
    if (!id || !s3Key) {
      return { status: 'ERROR', message: 'Context file details are missing.' }
    }

    if (Number(fileCount) <= 1) {
      return {
        status: 'ERROR',
        message:
          'Cannot remove the last file. Every product must keep at least one document.',
      }
    }

    return removeContextFile({ context_id: id, s3_key: s3Key })
  }

  async function renameContextFileRecord({ context_id, s3_key, filename }) {
    const id = String(context_id ?? '').trim()
    const s3Key = String(s3_key ?? '').trim()
    const nextName = String(filename ?? '').trim()
    if (!id || !s3Key || !nextName) {
      return { status: 'ERROR', message: 'Context file details are missing.' }
    }

    return editContextFile({
      context_id: id,
      s3_key: s3Key,
      filename: nextName,
    })
  }

  async function enableContextRecord(context) {
    const id = String(context?.context_id ?? '').trim()
    if (!id) {
      return { status: 'ERROR', message: 'Product id is missing.' }
    }
    return enableContext({ context_id: id })
  }

  async function disableContextRecord(context) {
    const id = String(context?.context_id ?? '').trim()
    if (!id) {
      return { status: 'ERROR', message: 'Product id is missing.' }
    }
    return disableContext({ context_id: id })
  }

  async function deleteContextRecord(context) {
    const id = String(context?.context_id ?? '').trim()
    if (!id) {
      return { status: 'ERROR', message: 'Product id is missing.' }
    }
    return deleteContext({ context_id: id })
  }

  function setPage(next) {
    page.value = Math.min(Math.max(1, next), pageCount.value)
  }

  function setItemsPerPage(value) {
    itemsPerPage.value = value
    page.value = 1
  }

  watch(filteredContexts, () => {
    if (page.value > pageCount.value) {
      page.value = pageCount.value
    }
  })

  watch(
    isSessionReady,
    (ready) => {
      if (ready) loadContexts()
    },
    { immediate: true },
  )

  watch(
    () => route.name,
    (name) => {
      if (name === 'Contexts' && isSessionReady.value) {
        loadContexts()
      }
    },
  )

  watch(tableSearch, () => {
    page.value = 1
  })

  return {
    contexts,
    loading,
    page,
    itemsPerPage,
    pageCount,
    isSessionReady,
    filteredContexts,
    paginatedContexts,
    totalContexts,
    activeCount,
    rangeLabel,
    isSearchActive,
    loadContexts,
    saveContext,
    removeContextFileRecord,
    renameContextFileRecord,
    enableContextRecord,
    disableContextRecord,
    deleteContextRecord,
    setPage,
    setItemsPerPage,
  }
}
